import { useState } from 'react'

import { Link } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { AtsCompanyActiveJobsDocument } from 'generated/resources/graphql'

import Callout from 'modules/core/components/Callout'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useFeatureAvailability } from 'modules/core/hooks/useFeatureAvailability'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import { useUpgradePlanModal } from 'modules/ats/lib/utils/useUpgradePlanModal'
import { ValidReasons } from 'modules/upsell/hooks/useCreateUpsellRequest'

import { UpsellPlan } from './UpsellPlan'

const UNLIMITED_JOBS = -1

export const ActiveJobsLimitWarning = () => {
  const [showUpsellModal, setShowUpsellModal] = useState(false)

  const { company } = useCurrentSession()

  const { featureAvailability } = useFeatureAvailability(Features.ats_active_jobs)
  const { data: companyActiveJobs } = useQuery(AtsCompanyActiveJobsDocument, {
    variables: { companyId: company.id },
  })

  const partnerManaged = companyActiveJobs?.features.featuresConnection.nodes[0]?.enabled
  const planInfo = companyActiveJobs?.ats.companiesConnection.nodes[0]
  const isLegacy = featureAvailability.usageLimit === null

  const legacyUsageLimit = planInfo?.planJobPostingsLimit ?? Infinity
  const legacyUsage = planInfo?.activeJobPostingsCount ?? 0
  const nonLegacyUsageLimit = featureAvailability.usageLimit ?? UNLIMITED_JOBS
  const nonLegacyUsage = featureAvailability.usage ?? 0

  const usageLimit = isLegacy ? legacyUsageLimit : nonLegacyUsageLimit
  const usage = isLegacy ? legacyUsage : nonLegacyUsage

  const warningMessage = limitMessage(usageLimit, usage)

  const { UpgradePlanModal, showUpgradeModal, toggleShowUpgradeModal } = useUpgradePlanModal()

  if (!warningMessage) return null

  const manualUpsellData = {
    requestReason: isLegacy ? ValidReasons.MissingFeature : ValidReasons.MissingFeatureLegacy,
    featureRequested: Features.ats_active_jobs,
    requestSource: 'Overlimits job opening creation',
  }

  return (
    <>
      <Callout
        description={warningMessage}
        compact
        link={
          partnerManaged ? undefined : (
            <Link.Touchable
              onClick={() => (isLegacy ? toggleShowUpgradeModal() : setShowUpsellModal(true))}
            >
              {i18n.t('ats.jobs.limits.upgrade_plan')}
            </Link.Touchable>
          )
        }
        type="warning"
      />
      {showUpgradeModal && (
        <UpgradePlanModal
          upsellingProps={manualUpsellData}
          origin={'createJobOpening'}
          onClose={toggleShowUpgradeModal}
        />
      )}
      {showUpsellModal && (
        <UpsellPlan
          onClose={() => setShowUpsellModal(false)}
          usageLimit={usageLimit}
          companyId={company.id}
        />
      )}
    </>
  )
}

const limitMessage = (usageLimit: number, usage: number) => {
  if (usageLimit === UNLIMITED_JOBS) return null

  const jobPostingsLeft = usageLimit - usage

  if (jobPostingsLeft <= 0) {
    return i18n.t('ats.jobs.limits.limit_reached_with_amount', { amount: usageLimit })
  }
  if (jobPostingsLeft === 1) {
    return i18n.t('ats.jobs.limits.amount_left', { published_jobs: usage, limit: usageLimit })
  }
}
